// ---------------------------------------------------------------------------
// TenantRoleSection — One role bucket (Data Creator or Data User) in the
// Tenant Group Roles admin page.
//
// Shows the role label as a heading, a count of assigned groups,
// dismissible chips for each assigned ACG, and a GroupSearch to add more.
// ---------------------------------------------------------------------------

import {
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  Subtitle2,
  TagGroup,
} from '@fluentui/react-components';

import { ACG_PICKER_GROUPS_SUFFIX } from '../../../constants/index.js';
import { GroupSearch } from '../../GroupSearch/GroupSearch.js';

import type { TenantRoleSectionProps } from './TenantRoleSection.interface.js';
import { useTenantRoleSectionStyles } from './TenantRoleSection.style.js';

export function TenantRoleSection({
  label,
  groups,
  service,
  onGroupAdd,
  onGroupRemove,
}: TenantRoleSectionProps) {
  const styles = useTenantRoleSectionStyles();

  return (
    <section className={styles.root} aria-label={label}>
      <div className={styles.header}>
        <Subtitle2 as="h3" className={styles.roleLabel}>
          {label}
        </Subtitle2>
        <span className={styles.groupCount}>
          {groups.length} {ACG_PICKER_GROUPS_SUFFIX}
        </span>
      </div>

      {/* Group search to add new security groups */}
      <GroupSearch service={service} onAdd={onGroupAdd} />

      {/* Assigned groups — side-by-side chips that wrap. Only the × button dismisses. */}
      {groups.length > 0 && (
        <TagGroup
          className={styles.groupList}
          onDismiss={(_e, data) => {
            onGroupRemove(data.value);
          }}
          aria-label={`${label} assigned groups`}
        >
          {groups.map((acg) => (
            <InteractionTag key={acg.id} value={acg.id}>
              <InteractionTagPrimary hasSecondaryAction>
                {acg.displayName ?? acg.id}
              </InteractionTagPrimary>
              <InteractionTagSecondary
                aria-label={`Remove ${acg.displayName ?? acg.id}`}
              />
            </InteractionTag>
          ))}
        </TagGroup>
      )}
    </section>
  );
}
